import { QueryClient } from "@tanstack/react-query";
import { fetchPrefetchBatch } from "../Dashboard/api";
import type { EntityKind, EntitySchema } from "../Dashboard/types";
import { ENTITY_QUERY_KEYS } from "./entity";
import { INTERACTION_QUERY_KEYS } from "./interactions";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

// Tunables.
// - Debounce while actively scrolling: long enough to span the natural gaps
//   between IntersectionObserver callbacks on a scrolling list so we coalesce
//   a whole gesture into one or two batches.
// - Debounce while idle: near-zero so page-load prefetch fires immediately.
// - Scroll-idle window: time since the last `scroll` event after which we
//   consider the user to have stopped scrolling.
const DEBOUNCE_WHILE_SCROLLING_MS = 250;
const DEBOUNCE_WHILE_IDLE_MS = 0;
const SCROLL_IDLE_WINDOW_MS = 150;
const MAX_BATCH_SIZE = 20;
const MAX_CONCURRENT_PREFETCHES = 2;
// How many times a single item may come back unresolved before we stop asking
// for it. Nothing here retries for its own sake — a flush is re-armed after
// every batch so overflow drains — so this is purely a termination guarantee.
// Without it an item that can never resolve is re-picked forever: the batch
// writes nothing to the cache, so the next flush sees it as still-missing and
// asks again, at the 0 ms idle debounce. That is #11190, measured at ~47
// requests/second for as long as the card stayed on screen.
const MAX_ATTEMPTS_PER_ITEM = 3;

export type WatcherEntityItem = {
  type: "entity";
  kind: EntityKind;
  id: string;
  timestamp: number;
};
export type WatcherInteractionItem = {
  type: "interaction";
  id: string;
  timestamp: number;
};
export type WatcherItem = WatcherEntityItem | WatcherInteractionItem;

const itemKey = (item: WatcherItem): string =>
  item.type === "entity"
    ? `entity:${item.kind}:${item.id}`
    : `interaction:${item.id}`;

const cacheKeyFor = (item: WatcherItem): readonly unknown[] =>
  item.type === "entity"
    ? ENTITY_QUERY_KEYS.entity(item.kind, item.id)
    : INTERACTION_QUERY_KEYS.detail(item.id);

// All prefetcher state hangs off the QueryClient rather than off the module
// (#12187): nothing owns module scope, so nothing can hand a test a fresh
// copy. In the app the QueryClient is the page singleton, so the "lifetime of
// the page" semantics below are unchanged; in the story suite, where every
// file shares one browser page, each story gets its own client and therefore
// its own abandonment set and debounce timer, instead of inheriting whatever
// the previous story left behind.
class Prefetcher {
  private readonly queryClient: QueryClient;

  // Each watcher owns an entry in watcherStates; flush merges them.
  private readonly watcherStates = new Map<string, WatcherItem[]>();
  private readonly inFlightKeys = new Set<string>();
  // Consecutive unresolved attempts per item key, and the keys that have used
  // them up. Both are keyed the same way as inFlightKeys. "Unresolved" covers
  // a rejected batch *and* a batch that resolved without the item in it — the
  // server answers a prefetch for a missing entity with a 200 that simply
  // omits it, so counting only rejections would swap a 404 loop for a 200
  // loop.
  //
  // Abandonment lasts the lifetime of the client (in the app, the page),
  // deliberately: prefetch only ever warms a cache, so the cost of not
  // retrying an item is one on-demand fetch when the user actually opens it,
  // while the cost of retrying is the loop this exists to stop.
  private readonly failedAttempts = new Map<string, number>();
  private readonly abandonedKeys = new Set<string>();
  private readonly pendingPromises: Promise<unknown>[] = [];
  private debounceTimer: ProductTimer | null = null;
  // Monotonic id bumped each time a flush is scheduled. flush() compares its
  // assigned id against the latest to detect that a newer flush has superseded
  // it while awaiting a concurrency slot — the older invocation then no-ops so
  // only the freshest one fires.
  private latestFlushId = 0;

  // Last scroll event seen anywhere in the window, plus a ref-count of mounted
  // watchers. One shared listener rather than one per hook: the handler only
  // stamps a timestamp, so N copies would do N times the work in the scroll
  // hot path for the same answer. The listener is added when the first watcher
  // mounts and removed when the last unmounts, so nothing outlives the hooks
  // that asked for it.
  private lastScrollAt = 0;
  private watcherRefCount = 0;
  private readonly onScroll = (): void => {
    this.lastScrollAt = Date.now();
  };

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  retain(): void {
    this.watcherRefCount += 1;
    if (this.watcherRefCount === 1 && typeof window !== "undefined") {
      // capture:true catches scrolls on nested scroll containers too, since
      // scroll events don't bubble. Passive keeps us out of the hot path.
      window.addEventListener("scroll", this.onScroll, {
        passive: true,
        capture: true,
      });
    }
  }

  release(): void {
    this.watcherRefCount -= 1;
    if (this.watcherRefCount > 0) return;
    if (typeof window !== "undefined") {
      window.removeEventListener("scroll", this.onScroll, { capture: true });
    }
    // Cleanup stops what the hooks started. In-flight fetches are left to
    // finish — they only write into the client's own cache — but no new flush
    // fires once the last watcher is gone.
    if (this.debounceTimer) {
      clearProductTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  publish(watcherId: string, items: WatcherItem[]): void {
    this.watcherStates.set(watcherId, items);
    this.armFlush();
  }

  unregister(watcherId: string): void {
    this.watcherStates.delete(watcherId);
  }

  private isUserScrolling(): boolean {
    return Date.now() - this.lastScrollAt < SCROLL_IDLE_WINDOW_MS;
  }

  private armFlush(): void {
    if (this.debounceTimer) clearProductTimeout(this.debounceTimer);
    const scheduledFlushId = ++this.latestFlushId;
    const delay = this.isUserScrolling()
      ? DEBOUNCE_WHILE_SCROLLING_MS
      : DEBOUNCE_WHILE_IDLE_MS;
    this.debounceTimer = setProductTimeout(() => {
      void this.flush(scheduledFlushId);
    }, delay);
  }

  private mergeWatcherStates(): WatcherItem[] {
    // Same key can appear in multiple watchers (e.g. a card rendered in both
    // the header recents and the dashboard body). Keep the latest timestamp.
    const merged = new Map<string, WatcherItem>();
    for (const items of this.watcherStates.values()) {
      for (const item of items) {
        const key = itemKey(item);
        const existing = merged.get(key);
        if (!existing || item.timestamp > existing.timestamp) {
          merged.set(key, item);
        }
      }
    }
    return Array.from(merged.values()).sort(
      (a, b) => b.timestamp - a.timestamp
    );
  }

  // An item counts as resolved only if the batch actually put it in the cache.
  // Re-deriving that from the cache (rather than from the response) keeps the
  // check honest for both shapes of miss and needs no id bookkeeping.
  private settleAttempts(picked: WatcherItem[]): void {
    for (const item of picked) {
      const key = itemKey(item);
      if (this.queryClient.getQueryData(cacheKeyFor(item))) {
        this.failedAttempts.delete(key);
        continue;
      }
      const attempts = (this.failedAttempts.get(key) ?? 0) + 1;
      if (attempts >= MAX_ATTEMPTS_PER_ITEM) {
        this.failedAttempts.delete(key);
        this.abandonedKeys.add(key);
      } else {
        this.failedAttempts.set(key, attempts);
      }
    }
  }

  private async flush(myId: number): Promise<void> {
    this.debounceTimer = null;

    // Concurrency gate. Because every wrapping promise we push resolves
    // (errors are caught below), Promise.any never rejects here.
    while (this.pendingPromises.length >= MAX_CONCURRENT_PREFETCHES) {
      await Promise.any(this.pendingPromises);
      if (myId !== this.latestFlushId) return;
    }
    if (myId !== this.latestFlushId) return;

    const client = this.queryClient;

    // Snapshot after the gate so any items that scrolled into view during
    // our await ride along in this batch instead of waiting another cycle.
    const merged = this.mergeWatcherStates();

    const picked: WatcherItem[] = [];
    for (const item of merged) {
      if (picked.length >= MAX_BATCH_SIZE) break;
      const key = itemKey(item);
      if (this.inFlightKeys.has(key)) continue;
      if (this.abandonedKeys.has(key)) continue;
      if (client.getQueryData(cacheKeyFor(item))) continue;
      picked.push(item);
    }

    if (picked.length === 0) return;

    const pickedKeys = picked.map(itemKey);
    pickedKeys.forEach((k) => this.inFlightKeys.add(k));

    const entities = picked
      .filter((item): item is WatcherEntityItem => item.type === "entity")
      .map((item) => ({ kind: item.kind, id: item.id }));
    const interactions = picked
      .filter(
        (item): item is WatcherInteractionItem => item.type === "interaction"
      )
      .map((item) => item.id);

    const request: Promise<void> = fetchPrefetchBatch({
      entities,
      interactions,
    })
      .then((response) => {
        response.entities.forEach((entity: EntitySchema) => {
          client.setQueryData(
            ENTITY_QUERY_KEYS.entity(entity.kind as EntityKind, entity.id),
            entity
          );
        });
        response.interactions.forEach((interaction) => {
          client.setQueryData(
            INTERACTION_QUERY_KEYS.detail(interaction.id),
            interaction
          );
        });
      })
      .catch((error) => {
        console.warn("RMS prefetch batch failed", error);
      })
      .finally(() => {
        this.settleAttempts(picked);
        const idx = this.pendingPromises.indexOf(request);
        if (idx !== -1) this.pendingPromises.splice(idx, 1);
        pickedKeys.forEach((k) => this.inFlightKeys.delete(k));
        // This batch finished but watcher state may still hold items that
        // didn't fit (MAX_BATCH_SIZE overflow) or arrived mid-flight. Arm
        // another flush so the remainder drains; if nothing's left, the next
        // flush exits at the empty-picked check. Nothing is re-armed once the
        // last watcher has unmounted — its cleanup owns the timer.
        if (this.watcherRefCount > 0) this.armFlush();
      });

    this.pendingPromises.push(request);
  }
}

const prefetcherFor = new WeakMap<QueryClient, Prefetcher>();

const getPrefetcher = (queryClient: QueryClient): Prefetcher => {
  const existing = prefetcherFor.get(queryClient);
  if (existing) return existing;
  const created = new Prefetcher(queryClient);
  prefetcherFor.set(queryClient, created);
  return created;
};

// Called by useVisibilityPrefetch's mount effect; the returned function is its
// cleanup. Holding a watcher is what keeps the shared scroll listener alive.
export const retainPrefetcher = (queryClient: QueryClient): (() => void) => {
  const prefetcher = getPrefetcher(queryClient);
  prefetcher.retain();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    prefetcher.release();
  };
};

export const publishWatcherState = (
  watcherId: string,
  queryClient: QueryClient,
  items: WatcherItem[]
): void => {
  getPrefetcher(queryClient).publish(watcherId, items);
};

export const unregisterWatcher = (
  watcherId: string,
  queryClient: QueryClient
): void => {
  prefetcherFor.get(queryClient)?.unregister(watcherId);
};
